/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './AccessRoutes.module.scss';

import { type AccessBackground, type AccessRoutesProps } from './AccessRoutes.types';

const BACKGROUNDS: Record<AccessBackground, string> = {
  navy: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(51,92,244,0.25), transparent 70%), linear-gradient(160deg, #042354 0%, #081d3d 100%)',
  midnight:
    'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(51,92,244,0.16), transparent 70%), linear-gradient(160deg, #0a0d14 0%, #000000 100%)',
  indigo:
    'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(5,80,226,0.28), transparent 70%), linear-gradient(160deg, #023285 0%, #01236e 100%)',
  light:
    'radial-gradient(ellipse 70% 55% at 12% -10%, rgba(5,80,226,0.14), transparent 60%), radial-gradient(ellipse 60% 50% at 100% 0%, rgba(56,66,205,0.10), transparent 65%), linear-gradient(160deg, #ffffff 0%, #f3f8ff 100%)',
};

export const AccessRoutes = ({
  id,
  routes,
  lede,
  kicker,
  heading = 'How to access the data.',
  background = 'navy',
  numbered = false,
  introCard = false,
  className,
}: AccessRoutesProps) => {
  const wayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const isLight = background === 'light';
  const isIntroDark = introCard || !isLight;

  useEffect(() => {
    const items = wayRefs.current.filter((item): item is HTMLDivElement => item !== null);

    if (items.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const index = items.indexOf(entry.target as HTMLDivElement);

          if (index !== -1) {
            setActiveIndex(index);
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [routes]);

  return (
    <Section
      id={id}
      background={BACKGROUNDS[background]}
      className={cn(styles.root, isLight && styles.light, className)}
    >
      <div className={styles.grid}>
        <div className={cn(styles.intro, introCard && styles.introCard)}>
          <TitleBlock
            align="left"
            theme={isIntroDark ? 'dark' : 'light'}
            accent="aurora"
            extra={kicker}
            title={<span>{heading}</span>}
            description={lede}
            className={styles.titleBlock}
          />
        </div>

        <div className={cn(styles.ways, numbered && styles.numbered)}>
          {routes.map((route, index) => (
            <div
              className={cn(styles.way, index === activeIndex && styles.wayActive)}
              ref={(el) => {
                wayRefs.current[index] = el;
              }}
              key={route.name}
            >
              <div className={styles.rail}>
                <span className={styles.dot}>{numbered ? String(index + 1).padStart(2, '0') : null}</span>
                <span className={styles.line} />
              </div>

              <div className={styles.wayBody}>
                <h4 className={styles.wayName}>{route.name}</h4>
                <p className={styles.wayDesc}>{route.desc}</p>

                {route.actions && route.actions.length > 0 && (
                  <div className={styles.wayActions}>
                    {route.actions.map((action) => (
                      <Button
                        type={isLight ? 'dark-outline' : 'white-outline'}
                        size="lg"
                        suffixIcon="arrow-14"
                        key={action.label}
                        {...(action.route ? { route: action.route } : { href: action.href as string })}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
